import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { X } from 'lucide-react'
import { RootState } from '../store'
import { setUser } from '../store/authSlice'
import authService from '../services/authService'
import usersService from '../services/usersService'
import { useTranslation } from '../hooks/use-i18n'
import { authUserId } from '../utils/userProfile'
import UserAvatar from './UserAvatar'

interface EditAccountModalProps {
  onClose: () => void
}

const EditAccountModal: React.FC<EditAccountModalProps> = ({ onClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const fileRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState(user?.name ?? '')
  const [photo, setPhoto] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setName(user?.name ?? '')
    setPhoto(null)
    setPreview(null)
  }, [user])

  useEffect(() => {
    if (!photo) return undefined
    const url = URL.createObjectURL(photo)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [photo])

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      toast.error(t('account.nameRequired'))
      return
    }
    const id = authUserId(user)
    if (!id) return
    setSaving(true)
    try {
      const formData = new FormData()
      formData.append('name', trimmed)
      if (photo) formData.append('avatar', photo)
      const updated = await usersService.update(id, formData)
      dispatch(setUser(updated))
      authService.persistUser(updated)
      toast.success(t('account.dataUpdated'))
      onClose()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : t('account.updateFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="glass-card rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h2 className="font-display text-xl font-bold text-foreground">{t('account.editAccount')}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t('account.editAccountDesc')}</p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" aria-label={t('common.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <label className="block text-sm font-medium text-foreground mb-2">{t('account.selectPhoto')}</label>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full rounded-2xl border border-border bg-muted/20 hover:bg-muted/30 transition-colors p-4 flex flex-col items-center gap-3 mb-5"
        >
          {preview ? (
            <img src={preview} alt="" className="w-28 h-28 rounded-full object-cover" />
          ) : (
            <UserAvatar name={user?.name} avatar={user?.avatar} size={112} />
          )}
          <span className="text-sm text-primary font-medium">{t('account.selectPhoto')}</span>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/jpg"
          className="hidden"
          onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
        />

        <label className="block text-sm font-medium text-foreground mb-1">{t('account.name')}</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground mb-6"
        />

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-muted-foreground hover:bg-muted">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium disabled:opacity-50"
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditAccountModal
